/**
 * Chargebee event types grouped by category.
 * Used for the event type filter dropdown.
 */

export const EVENT_TYPE_CATEGORIES: Record<string, string[]> = {
  Customer: [
    'customer_created',
    'customer_changed',
    'customer_deleted',
    'customer_moved_in',
    'customer_moved_out',
    'customer_business_entity_changed',
  ],
  Subscription: [
    'subscription_created',
    'subscription_started',
    'subscription_trial_end_reminder',
    'subscription_activated',
    'subscription_changed',
    'subscription_cancellation_scheduled',
    'subscription_cancellation_reminder',
    'subscription_cancelled',
    'subscription_reactivated',
    'subscription_renewed',
    'subscription_scheduled_cancellation_removed',
    'subscription_changes_scheduled',
    'subscription_scheduled_changes_removed',
    'subscription_shipping_address_updated',
    'subscription_deleted',
    'subscription_paused',
    'subscription_pause_scheduled',
    'subscription_scheduled_pause_removed',
    'subscription_resumed',
    'subscription_resumption_scheduled',
    'subscription_scheduled_resumption_removed',
    'subscription_advance_invoice_schedule_added',
    'subscription_advance_invoice_schedule_updated',
    'subscription_advance_invoice_schedule_removed',
    'subscription_renewal_reminder',
    'subscription_moved_in',
    'subscription_moved_out',
    'subscription_business_entity_changed',
  ],
  Invoice: [
    'invoice_generated',
    'invoice_updated',
    'invoice_deleted',
    'pending_invoice_created',
    'pending_invoice_updated',
  ],
  Payment: [
    'payment_succeeded',
    'payment_failed',
    'payment_refunded',
    'payment_initiated',
  ],
  'Payment Source': [
    'payment_source_added',
    'payment_source_updated',
    'payment_source_deleted',
    'payment_source_expiring',
    'payment_source_expired',
    'payment_source_locally_deleted',
  ],
  Card: [
    'card_added',
    'card_updated',
    'card_expiry_reminder',
    'card_expired',
    'card_deleted',
  ],
  Transaction: [
    'transaction_created',
    'transaction_updated',
    'transaction_deleted',
  ],
  'Credit Note': [
    'credit_note_created',
    'credit_note_updated',
    'credit_note_deleted',
  ],
  Order: [
    'order_created',
    'order_updated',
    'order_cancelled',
    'order_delivered',
    'order_returned',
    'order_ready_to_process',
    'order_ready_to_ship',
    'order_deleted',
    'order_resent',
  ],
  Quote: ['quote_created', 'quote_updated', 'quote_deleted'],
  Plan: ['plan_created', 'plan_updated', 'plan_deleted'],
  Addon: ['addon_created', 'addon_updated', 'addon_deleted'],
  Coupon: [
    'coupon_created',
    'coupon_updated',
    'coupon_deleted',
    'coupon_set_created',
    'coupon_set_updated',
    'coupon_set_deleted',
    'coupon_codes_added',
    'coupon_codes_deleted',
    'coupon_codes_updated',
  ],
  Item: [
    'item_created',
    'item_updated',
    'item_deleted',
    'item_family_created',
    'item_family_updated',
    'item_family_deleted',
    'item_price_created',
    'item_price_updated',
    'item_price_deleted',
  ],
  'Contract Term': [
    'contract_term_created',
    'contract_term_renewed',
    'contract_term_terminated',
    'contract_term_completed',
    'contract_term_cancelled',
  ],
  Gift: [
    'gift_scheduled',
    'gift_unclaimed',
    'gift_claimed',
    'gift_expired',
    'gift_cancelled',
    'gift_updated',
  ],
  'Virtual Bank Account': [
    'virtual_bank_account_added',
    'virtual_bank_account_updated',
    'virtual_bank_account_deleted',
  ],
  Tax: [
    'tax_withheld_created',
    'tax_withheld_updated',
    'tax_withheld_deleted',
    'tax_withheld_refunded',
  ],
  Authorization: [
    'authorization_created',
    'authorization_voided',
    'authorization_expired',
  ],
};

/**
 * Flat list of all event types for search/validation.
 */
export const ALL_EVENT_TYPES: string[] = Object.values(
  EVENT_TYPE_CATEGORIES
).flat();

/**
 * Human-readable label for an event type.
 * e.g. 'subscription_created' → 'Subscription Created'
 */
export function formatEventType(eventType: string): string {
  return eventType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
